export const loadBlockComponent = (editor, options) => {
  const componentType = options.componentType || 'Block';
  const locale = new URLSearchParams(window.location.search).get('locale');
  const localeParam = locale ? `&locale=${locale}` : '';

  editor.Components.addType(componentType, {
    model: {
      defaults: {
        name: options.label || 'Block',
        tagName: 'div',
        droppable: false,
        editable: false,
        stylable: false,
        resizable: false,
        copyable: true,
        attributes: {
          'data-gjs-type': componentType,
        },
        traits: [
          {
            type: 'autocomplete',
            name: 'data-block-id',
            label: 'Block',
            model: 'block',
            placeholder: 'Search blocks...',
            minLength: 0,
            locale: locale || '',
          },
          {
            type: 'button',
            name: 'edit-block',
            label: 'Edit block',
            text: 'Open block editor',
            full: true,
            command: (editor) => {
              const selected = editor.getSelected();
              const blockId = selected?.getAttributes()['data-block-id'];
              if (blockId) {
                const localeQuery = locale ? `?locale=${locale}` : '';
                window.open(`/nv/admin/blocks/${blockId}/builder${localeQuery}`, '_blank');
              } else {
                alert(I18n.t('admin.block_builder.placeholder.select_first'));
              }
            },
          },
        ],
      },

      init() {
        this.on('change:attributes:data-block-id', this.onBlockIdChange);
        this.listenTo(this, 'change:view', this.onViewReady);
      },

      isValidBlockId(id) {
        return id && /^[1-9]\d*$/.test(id);
      },

      onViewReady() {
        const blockId = this.getAttributes()['data-block-id'];
        if (this.isValidBlockId(blockId)) {
          this.loadBlockContent(blockId);
        } else {
          this.setPlaceholderContent();
        }
      },

      onBlockIdChange() {
        const blockId = this.getAttributes()['data-block-id'];
        if (this.isValidBlockId(blockId) && this.view && this.view.onRender) {
          this.view.onRender();
        }
      },

      async loadBlockContent(blockId) {
        if (!this.isValidBlockId(blockId)) {
          this.setPlaceholderContent();
          return;
        }

        try {
          const response = await fetch(`/nv/admin/blocks/${blockId}/body?published=true${localeParam}`);
          if (!response.ok) {
            throw new Error('Failed to load block');
          }

          const data = await response.json();
          const html = data.html || '';

          if (html) {
            this.set('blockHtml', html);
            this.renderBlockPreview(html);
          } else {
            this.setPlaceholderContent(I18n.t('admin.block_builder.placeholder.no_content'));
          }
        } catch (error) {
          console.error('Error loading block content:', error);
          this.setPlaceholderContent(I18n.t('admin.block_builder.placeholder.error'));
        }
      },

      renderBlockPreview(html) {
        const view = this.view;
        if (view && view.el) {
          view.el.innerHTML = html;
        }
      },

      setPlaceholderContent(message = I18n.t('admin.block_builder.placeholder.select')) {
        const view = this.view;
        if (view && view.el) {
          view.el.textContent = message;
        }
      },

      toHTML() {
        const id = this.getAttributes()['data-block-id'];
        const props = { id: id || '' };
        const escapedProps = JSON.stringify(props).replace(/"/g, '&quot;');

        return `<div data-react-class="Block" data-react-props="${escapedProps}" data-react-cache-id="Block-${this.ccid}"></div>`;
      },
    },

    isComponent(el) {
      if (el.tagName !== 'DIV') return false;

      if (el.getAttribute('data-react-class') === 'Block') {
        try {
          const propsAttr = el.getAttribute('data-react-props');
          if (propsAttr) {
            const props = JSON.parse(propsAttr);
            return {
              type: componentType,
              attributes: {
                'data-block-id': props.id || '',
              },
            };
          }
        } catch (e) {
          console.warn('Failed to parse Block props:', e);
        }
        return { type: componentType };
      }

      return false;
    },

    view: {
      async onRender() {
        const model = this.model;
        const el = this.el;
        const blockId = model.getAttributes()['data-block-id'];

        if (!blockId || !/^[1-9]\d*$/.test(blockId)) {
          el.innerHTML = this.getPlaceholderHtml(I18n.t('admin.block_builder.placeholder.select_panel'));
          return;
        }

        try {
          const response = await fetch(`/nv/admin/blocks/${blockId}/body?published=true${localeParam}`);
          if (!response.ok) {
            throw new Error('Failed to load block');
          }

          const data = await response.json();
          const html = data.html || '';

          if (html) {
            el.innerHTML = html;
            el.style.cursor = 'pointer';
            el.addEventListener('dblclick', () => {
              const localeQuery = locale ? `?locale=${locale}` : '';
              window.open(`/nv/admin/blocks/${blockId}/builder${localeQuery}`, '_blank');
            });
            this.hydrateReactComponents(el);
          } else {
            el.innerHTML = this.getPlaceholderHtml(I18n.t('admin.block_builder.placeholder.no_content'));
          }
        } catch (error) {
          console.error('Error loading block content:', error);
          el.innerHTML = this.getPlaceholderHtml(I18n.t('admin.block_builder.placeholder.error'));
        }
      },

      hydrateReactComponents(container) {
        if (typeof ReactRailsUJS !== 'undefined' && ReactRailsUJS.mountComponents) {
          try {
            ReactRailsUJS.mountComponents(container);
          } catch (error) {
            console.warn('Failed to hydrate React components in block:', error);
          }
          return;
        }

        const reactElements = container.querySelectorAll('[data-react-class]');
        reactElements.forEach((el) => {
          const componentName = el.getAttribute('data-react-class');
          el.innerHTML = `
            <div style="padding: 15px; text-align: center; border: 1px dashed #2196F3; background: #e3f2fd; color: #1565c0; font-size: 0.9em;">
              <strong>${componentName}</strong>
              <div style="font-size: 0.8em; margin-top: 5px;">React component - renders on frontend</div>
            </div>
          `;
        });
      },

      getPlaceholderHtml(message) {
        return message;
      },
    },
  });
};
